'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import ReactMarkdown from 'react-markdown'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Clock01Icon,
  ZapIcon,
  MoreHorizontalIcon,
  Cancel01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { NewCommandPopover } from './NewCommandPopover'
import {
  FALLBACK_COMMANDS,
  type AutomationChannel,
  type CommandIconKey,
  type CommandRow,
} from '@/lib/automations/fallback-data'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { AuthGateModal } from '@/components/auth/AuthGateModal'
import { Skeleton } from '@/components/ui/skeleton'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useCachedUser } from '@/hooks/useCachedUser'
import {
  COMMAND_ICON,
  type CommandFormState,
} from './CommandFormPage'
import { CommandsEmptyState } from './CommandsEmptyState'
import { cn } from '@/lib/utils'

type CommandView = {
  id: string
  key: string
  name: string
  description: string
  prompt: string
  iconKey: CommandIconKey
  channel: AutomationChannel
  execution_mode: CommandFormState['execution_mode']
  model_key: string | null
  allowed_tools: string[]
  chat_invocable: boolean
  chat_invocation_hint: string
  deterministic_graph: Record<string, unknown> | null
  trigger_type: CommandFormState['trigger_type']
  trigger_config: Record<string, unknown>
  enabled: boolean
  prompt_health: 'unknown' | 'ok' | 'warning' | 'error'
  last_validation_result: {
    errors?: unknown[]
    warnings?: unknown[]
  } | null
  cost_cents_last_30d: number
  lastRun: string
}

function formatCost(cents: number): string {
  return `$${(Math.max(0, cents) / 100).toFixed(2)}`
}

function fallbackPrompt(command: CommandRow) {
  return `${command.name}\n\n${command.description}\n\nReturn a concise, self-contained recommendation.`
}

function fromApi(row: any): CommandView {
  return {
    id: row.id,
    key: row.id,
    name: row.name,
    description: row.description || '',
    prompt: row.prompt || '',
    iconKey: row.icon_key || 'sparkles',
    channel: row.channel || 'workspace',
    execution_mode: row.execution_mode || 'ai',
    model_key: row.model_key || 'auto',
    allowed_tools: Array.isArray(row.allowed_tools) ? row.allowed_tools : [],
    chat_invocable: row.chat_invocable === true,
    chat_invocation_hint: row.chat_invocation_hint || '',
    deterministic_graph: row.deterministic_graph || null,
    trigger_type: row.trigger_type || 'manual',
    trigger_config: row.trigger_config || {},
    enabled: row.enabled !== false,
    prompt_health: row.prompt_health === 'ok' || row.prompt_health === 'warning' || row.prompt_health === 'error' ? row.prompt_health : 'unknown',
    last_validation_result: row.last_validation_result && typeof row.last_validation_result === 'object' ? row.last_validation_result : null,
    cost_cents_last_30d: Number(row.cost_cents_last_30d ?? 0),
    lastRun: row.last_run || 'Never',
  }
}

function fallbackRows(commands: CommandRow[]): CommandView[] {
  return commands.map((command) => ({
    id: command.key,
    key: command.key,
    name: command.name,
    description: command.description,
    prompt: fallbackPrompt(command),
    iconKey: command.iconKey,
    channel: 'workspace',
    execution_mode: 'ai',
    model_key: 'auto',
    allowed_tools: [],
    chat_invocable: false,
    chat_invocation_hint: '',
    deterministic_graph: null,
    trigger_type: 'manual',
    trigger_config: {},
    enabled: true,
    prompt_health: 'unknown',
    last_validation_result: null,
    cost_cents_last_30d: 0,
    lastRun: command.lastRun,
  }))
}

function PromptHealthPill({ command }: { command: CommandView }) {
  if (command.prompt_health !== 'error' && command.prompt_health !== 'warning') return null
  const errors = Array.isArray(command.last_validation_result?.errors) ? command.last_validation_result.errors.length : 0
  const warnings = Array.isArray(command.last_validation_result?.warnings) ? command.last_validation_result.warnings.length : 0
  const isError = command.prompt_health === 'error'
  const tooltip = isError
    ? `${errors} ${errors === 1 ? 'error' : 'errors'}${warnings ? `, ${warnings} ${warnings === 1 ? 'warning' : 'warnings'}` : ''}`
    : `${warnings} ${warnings === 1 ? 'warning' : 'warnings'}`

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={[
              'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
              isError ? 'bg-red-50 text-red-700 ring-1 ring-red-200' : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
            ].join(' ')}
          >
            <span aria-hidden>{isError ? '🔴' : '🟡'}</span>
            {isError ? 'Prompt issues' : 'Prompt warnings'}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function CommandSkeletonCard() {
  return (
    <div className="bv3-cmdcard">
      <Skeleton className="size-9 rounded-md" />
      <Skeleton className="mt-2 h-4 w-3/4" />
      <Skeleton className="mt-1 h-3 w-full" />
      <Skeleton className="mt-1 h-3 w-2/3" />
      <Skeleton className="mt-3 h-3 w-1/3" />
      <Skeleton className="mt-2 h-7 w-16 rounded-full" />
    </div>
  )
}

export function CommandsV3Page({ commands }: { commands: CommandRow[] }) {
  const router = useRouter()
  const { user } = useCachedUser()
  const [rows, setRows] = useState<CommandView[]>(() => fallbackRows(commands.length ? commands : FALLBACK_COMMANDS))
  const [loaded, setLoaded] = useState(false)
  const [menuOpen, setMenuOpen] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [runningId, setRunningId] = useState<string | null>(null)
  const [runPanel, setRunPanel] = useState<{ title: string; response: string } | null>(null)
  const [authModalOpen, setAuthModalOpen] = useState(false)

  async function load() {
    const res = await fetch('/api/commands')
    if (!res.ok) {
      setLoaded(true)
      return
    }
    const data = await res.json()
    setRows((data.commands || []).map(fromApi))
    setLoaded(true)
  }

  useEffect(() => { void load() }, [])

  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const headerText = useMemo(() => `${rows.length} commands · run on demand or schedule`, [rows.length])

  async function deleteCommand(command: CommandView) {
    if (!window.confirm(`Delete ${command.name}?`)) return
    const res = await fetch(`/api/commands/${command.id}`, { method: 'DELETE' })
    if (!res.ok) {
      alert(await res.text())
      return
    }
    setRows((prev) => prev.filter((row) => row.id !== command.id))
  }

  async function runCommand(command: CommandView) {
    if (!user) {
      setAuthModalOpen(true)
      return
    }
    setRunningId(command.id)
    const res = await fetch(`/api/commands/${command.id}/run`, { method: 'POST' })
    const data = await res.json().catch(() => ({}))
    setRunningId(null)
    if (!res.ok || data.status === 'failed') {
      alert(data.error || 'Command failed')
      return
    }
    setRunPanel({ title: command.name, response: data.response || '' })
  }

  return (
    <div className="min-h-full">
      <LobbyHeaderMeta>{headerText}</LobbyHeaderMeta>

      <div className="max-w-4xl mx-auto pt-12 pb-20">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Commands</h1>
          <NewCommandPopover />
        </div>

        {loaded && rows.length === 0 ? (
          <CommandsEmptyState />
        ) : !loaded ? (
          <div className="bv3-cmdgrid">
            {Array.from({ length: 8 }, (_, index) => <CommandSkeletonCard key={index} />)}
          </div>
        ) : (
          <TooltipProvider>
            <div className="bv3-cmdgrid">
            {rows.map((c) => (
            <div
              key={c.key}
              className={cn('bv3-cmdcard relative cursor-pointer', !c.enabled && 'opacity-50 grayscale')}
              onClick={() => router.push(`/commands/${c.id}`)}
              onContextMenu={(e) => { e.preventDefault(); setMenuOpen(c.id) }}
            >
              <div
                ref={menuOpen === c.id ? menuRef : undefined}
                className="absolute right-3 top-3"
              >
                <button
                  type="button"
                  className="rounded-lg p-1 text-atrium-black/50 hover:bg-atrium-content hover:text-atrium-black"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === c.id ? null : c.id) }}
                >
                  <HugeiconsIcon icon={MoreHorizontalIcon} className="size-4" />
                </button>
                {menuOpen === c.id && (
                  <div className="absolute right-0 top-8 z-10 w-32 rounded-xl border border-atrium-border bg-white p-1 text-sm text-atrium-black shadow-lg">
                    <button
                      type="button"
                      className="block w-full rounded-lg px-3 py-2 text-left text-red-600 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(null); void deleteCommand(c) }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div className="tile">
                <HugeiconsIcon icon={COMMAND_ICON[c.iconKey]} className="size-5" />
              </div>
              <div className="flex items-center gap-2 pr-8">
                <div className="nm min-w-0 truncate">{c.name}</div>
                {!c.enabled && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="shrink-0 rounded-full border border-atrium-border bg-atrium-content px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-atrium-muted"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Disabled
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>This command is disabled and won&apos;t run for any triggers</TooltipContent>
                  </Tooltip>
                )}
              </div>
              <div className="desc">{c.description}</div>
              <PromptHealthPill command={c} />
              <div className="meta2">
                <HugeiconsIcon icon={Clock01Icon} className="size-3" />
                Last run {c.lastRun}
              </div>
              <div className="mt-2 inline-flex w-fit rounded-full border border-atrium-border bg-atrium-content px-2 py-0.5 text-[11px] font-medium text-atrium-muted">
                {formatCost(c.cost_cents_last_30d)} last 30d
              </div>
              <button type="button" className="run" disabled={runningId === c.id} onClick={(e) => { e.stopPropagation(); void runCommand(c) }}>
                <HugeiconsIcon icon={ZapIcon} className="size-3.5" />
                {runningId === c.id ? 'Running...' : 'Run'}
              </button>
            </div>
            ))}
            </div>
          </TooltipProvider>
        )}
      </div>

      {runPanel && (
        <SlideOutPanel open onOpenChange={(open) => !open && setRunPanel(null)} direction="right" className="md:max-w-xl">
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-atrium-black">{runPanel.title}</h2>
                <p className="text-sm text-atrium-black/50">Command response</p>
              </div>
              <button type="button" className="rounded-lg p-2 text-atrium-black/50 hover:bg-atrium-content" onClick={() => setRunPanel(null)}>
                <HugeiconsIcon icon={Cancel01Icon} className="size-5" />
              </button>
            </div>
            <div className="prose prose-sm max-w-none text-atrium-black">
              <ReactMarkdown>{runPanel.response}</ReactMarkdown>
            </div>
          </div>
        </SlideOutPanel>
      )}
      <AuthGateModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        title="Sign in to run commands"
        body="Create an account or sign in to run saved commands, schedule automations, and keep a history of each response."
        icon={ZapIcon}
        loginHref="/login?next=/commands"
        signupHref="/signup?next=/commands"
        bodyId="commands-gate-body"
      />
    </div>
  )
}
